import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { App, Flex, Space, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect, useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import Button from "../../../Components/Button"
import EmptyComponent from "../../../Components/Empty"
import Filters from "../../../Components/Filters"
import GlobalFilterTestData from "../../../Components/GlobalFilterTestData/globalFilterTest"
import ManageColumn from "../../../Components/ManageColumns"
import Table from "../../../Components/Table"
import { getBaseColumns } from "../../../Components/Table/baseColumns"
import Icon from "../../../Icon"
import { modalConfig } from "../../../theme"
import { serializeData } from "../../../Utils/utils"
import { useUpdatePreferences } from "../../User/hooks/useUpdatePreferences"
import PayoutMethodDrawer from "./components/PayoutMethodDrawer/PayoutMethodDrawer"
import { getActionColumn, getColumns, getDefaultColumn } from "./components/Table/columns"
import {
  REQUESTS_WITHDRAWAL_PREFERENCES_VERSION,
  requestsWithdrawalActions,
  selectAreFiltersDefault
} from "./redux/requestsWithdrawal.slice"
import {
  deleteRequestsWithdrawal,
  getRequestsWithdrawal,
  setStatus
} from "./redux/requestsWithdrawal.thunks"

import "./styles.less"

const pageSize = 50

const { Text, Title } = Typography

const pinnedColumns = ["Status"]

const RequeststWithdrawalPage = ({
  isMobile,
  businessAccountId,
  visibleFiltersAdmin,
  defaultColumnsAdmin,
  columnsAdmin,
  fetching,
  result,
  filters,
  statistics,
  filterOptions,
  test_data,
  ...props
}) => {
  const dispatch = useDispatch()

  const navigate = useNavigate()
  const { t } = useTranslation()
  const { notification, modal } = App.useApp()

  const [pageNumber, setPageNumber] = useState(1)
  const [ordering, setOrdering] = useState(null)

  const [openedRequestId, setOpenedRequestId] = useState(null)

  const isFiltersDefault = useSelector(selectAreFiltersDefault)

  const { count, results } = result || {}
  const myColumns = columnsAdmin
  const allFilters = defaultColumnsAdmin
  const visibleFilters = visibleFiltersAdmin

  const myColumnsLength = myColumns.filter(({ active }) => active).length
  const myColumnsHash = myColumns
    .filter(({ active }) => active)
    .map(({ field_id }) => field_id)
    .join("")

  const preferencesData = useMemo(
    () => ({
      version: REQUESTS_WITHDRAWAL_PREFERENCES_VERSION,
      filters,
      columnsAdmin,
      visibleFiltersAdmin
    }),
    [filters, columnsAdmin, visibleFiltersAdmin]
  )
  useUpdatePreferences("requests-withdrawal", preferencesData)

  const statisticsFolder = statistics?.["integrations_counter"] || 0

  const notificationConfig = {
    closeIcon: <Text>Dismiss</Text>,
    style: { minWidth: "max-content", width: 475 }
  }

  const prevTestDataRef = useRef(test_data)

  const updateData = () => {
    const serializeFilters = serializeData(filters)
    const columns = myColumns.filter(({ active }) => active).map(({ field_id }) => field_id)
    const data = {
      page: pageNumber,
      page_size: pageSize,
      ordering,
      ...serializeFilters,
      test_data,
      ...(columns.length && { columns: ["status", "id", ...columns].join(",") })
    }
    props.getInfo({ ...data })
  }

  const onStatusChange = ({ id, newStatus }) => {
    dispatch(setStatus({ id, status: newStatus }))
  }

  useEffect(() => {
    document.title = "Requests Integrations - Chargeback"

    const testDataChanged = prevTestDataRef.current !== test_data
    if (testDataChanged) {
      prevTestDataRef.current = test_data
      if (pageNumber !== 1) {
        setPageNumber(1)
        return
      }
    }

    updateData()
  }, [businessAccountId, ordering, pageNumber, filters, test_data, myColumnsLength])

  const tableSettings = useMemo(
    () => ({
      scroll: {
        //x: (230 * myColumns.filter(({active}) => active).length),
        x: count ? "max-content" : 230 * myColumns.filter(({ active }) => active).length,
        y: "calc(100vh - 72px - 24px - 32px - 24px - 43px - (16px + 32px + 16px) - 16px)",
        scrollToFirstRowOnChange: true
      },
      pagination: {
        total: count,
        current: pageNumber,
        pageSize: pageSize
      },
      locale: {
        emptyText: !statisticsFolder ? (
          <EmptyComponent type="requests_integrations_admin" />
        ) : (
          <EmptyComponent type="search">
            <Button
              className="flex"
              icon={<Icon type={"menu_search"} />}
              onClick={props.clearFilters}
            >
              Clear search
            </Button>
          </EmptyComponent>
        )
      }
    }),
    [myColumnsLength, count, pageNumber, statisticsFolder]
  )

  const getTableColumns = useMemo(() => {
    let allColumns = {
      ...getBaseColumns({ onSelectRow: handleSelectRow, isAdmin: true }),
      ...getColumns({ statuses: filterOptions?.status || [], onStatusChange })
    }
    return [
      allColumns["status"],
      ...myColumns
        .filter(({ active }) => active)
        .map((data) => allColumns?.[data.field_id] || getDefaultColumn({ ...data })),
      getActionColumn({ onSelectRow: handleSelectRow })
    ]
  }, [myColumnsHash])

  const handleChangeTable = useCallback((pagination, filters, sorter) => {
    let ordering = null
    if (sorter) {
      if (Array.isArray(sorter)) {
        ordering = sorter
          ?.map(({ field, order }) => (order === "ascend" ? field : `-${field}`))
          .join(",")
      } else {
        if (sorter.order) {
          ordering = sorter.order === "ascend" ? sorter.field : `-${sorter.field}`
        }
      }
    }
    setPageNumber(pagination.current)
    setOrdering(ordering)
  }, [])

  const handleChangeFilter = useCallback((newData, type) => {
    if (type === "changeVisible") {
      props.changeVisibleFilters("visibleFiltersAdmin", newData)
    } else {
      props.changeFilters(newData)
      setPageNumber(1)
    }
  }, [])

  const handleChangeColumns = useCallback((newColumns) => {
    props.changeColumns("columnsAdmin", newColumns)
  }, [])

  function handleSelectRow(data, type) {
    if (type === "delete") {
      modal.confirm({
        ...modalConfig("exclamation"),
        centered: !isMobile,

        title: "Confirm deleting",
        content: "Are you sure you want to delete? This action can NOT be undone.",

        cancelText: "Cancel",
        okText: "Delete",
        okButtonProps: {
          ...modalConfig("exclamation")?.okButtonProps,
          danger: true,
          ghost: true
        },
        async onOk() {
          await props.deleteRequest({ data }).unwrap()
          updateData()
        },
        onCancel() {
          console.log("Cancel")
        }
      })
    }
  }

  const getRowClassName = useCallback((record) => {
    return record.type === "Outsider" ? "clickable-row" : ""
  }, [])

  const onRow = useCallback((record) => {
    return {
      onClick: () => {
        if (record.type === "Outsider") {
          setOpenedRequestId(record.id)
        }
      }
    }
  }, [])

  return (
    <div className="requests-page">
      <Title level={3} style={{ textAlign: "left", marginBottom: 16 }}>
        {`${t("Withdrawal requests")}: ${count ?? 0}`}
      </Title>

      <Flex gap={32} align="center" justify="space-between" style={{ margin: "0 0 20px" }}>
        <Filters
          visibleFilters={visibleFilters}
          filterOptions={filterOptions}
          allFilters={allFilters}
          filters={filters}
          onChangeFilter={handleChangeFilter}
          filtersType={"withdrawal"} //for rename some fields labels in filtersTypeByIdMap
          showClearBtn={!isFiltersDefault}
          onClearFilters={props.clearFilters}
        />

        <Space style={{ flexShrink: 0 }}>
          <GlobalFilterTestData setPageNumber={setPageNumber} />

          <ManageColumn
            myColumns={myColumns}
            onChangeColumns={handleChangeColumns}
            pinnedColumns={pinnedColumns}
          />
        </Space>
      </Flex>

      <Table
        columns={getTableColumns}
        // loading={fetching}
        dataSource={results}
        scroll={tableSettings.scroll}
        onChange={handleChangeTable}
        pagination={tableSettings.pagination}
        showSizeChanger={false}
        //withOutPagination={true}
        locale={tableSettings.locale}
        onRow={onRow}
        rowClassName={getRowClassName}
      />

      <PayoutMethodDrawer id={openedRequestId} close={() => setOpenedRequestId(null)} />
    </div>
  )
}

const mapStateToProps = (state) => ({
  businessAccountId: state.auth.userInfo.current_business_account_id,
  isMobile: state.nav.isMobile,

  visibleFiltersAdmin: state.requestsWithdrawal.visibleFiltersAdmin,

  defaultColumnsAdmin: state.requestsWithdrawal.defaultColumnsAdmin,

  columnsAdmin: state.requestsWithdrawal.columnsAdmin,

  fetching: state.requestsWithdrawal.fetching,
  result: state.requestsWithdrawal.result,

  filters: state.requestsWithdrawal.filters,
  filterOptions: state.requestsWithdrawal.filterOptions,

  statistics: state.businessAccounts.statistics,
  test_data: state.globalFilters.testData
})

const mapDispatchToProps = (dispatch) => ({
  getInfo: (params) => dispatch(getRequestsWithdrawal(params)),
  deleteRequest: (params) => dispatch(deleteRequestsWithdrawal(params)),

  changeColumns: (key, columns) =>
    dispatch(requestsWithdrawalActions.requestsWithdrawalSetProp({ key, value: columns })),
  changeFilters: (filters) =>
    dispatch(
      requestsWithdrawalActions.requestsWithdrawalSetProp({ key: "filters", value: filters })
    ),
  changeVisibleFilters: (visibleFiltersType, filters) =>
    dispatch(
      requestsWithdrawalActions.requestsWithdrawalSetProp({
        key: visibleFiltersType,
        value: filters
      })
    ),
  clearFilters: () => dispatch(requestsWithdrawalActions.clearRequestsWithdrawalFilters())
})

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(RequeststWithdrawalPage))
